<template>
   <PageLoader :isLoading="isLoading" :loadErrors="loadErrors" :loadErrorMessage="loadErrorMessage">
      <div
         v-if="state.report"
         class="trait-report container flex flex-col content-center items-center"
         :data-cy="`${state.report.key}-report`"
      >
         <!-- Header  section -->
         <div class="mb-10 mt-20 flex flex-col items-center text-center">
            <p class="rounded-2xl border px-2 text-sm capitalize text-brand-dark">
               {{ readableCategory }}
            </p>
            <h1 class="h1 mt-6">{{ state.report.title }}</h1>
            <p class="mt-14 max-w-md">{{ state.report.subtitle }}</p>
         </div>
         <TraitReportCard
            v-if="showPersonalisation"
            :reportCard="state.report.cardContent"
            :reportSnpInfo="state.report.snpInfo"
            :userVariants="state.snps"
            :reportKey="state.report.key"
            :heritability="state.report.heritability"
            :isConditionReport="isConditionReport"
         />
         <RichTextRenderer class="max-w-[660px]" :context="getSnpContext()" :document="state.report.content" />
         <!-- References and Glossary section -->
         <div class="mt-20 rounded-xl bg-brand-dark p-10 text-white md:p-20">
            <section class="refs-and-glossary-section">
               <h2 class="h2 basis-1/3">{{ $t("report.references") }}</h2>
               <div class="flex basis-2/3 flex-col flex-wrap gap-5">
                  <a
                     v-for="(reference, i) in state.report.references"
                     :id="`ref-${i + 1}`"
                     :key="`reference-${i}`"
                     :href="reference.url"
                     target="_blank"
                     ><span class="text-bolder">{{ `[${i + 1}] ` }}</span
                     >{{ reference.title }}</a
                  >
               </div>
            </section>
            <hr class="my-20" />
            <section class="refs-and-glossary-section">
               <h2 class="h2 basis-1/3">{{ $t("report.glossary") }}</h2>
               <div class="flex basis-2/3 flex-col flex-wrap gap-5">
                  <div v-if="state.report.information">
                     <p class="text-bolder">{{ `[${state.report.information.SNP.title}] ` }}</p>
                     <p>{{ state.report.information.SNP.text }}</p>
                  </div>
               </div>
            </section>
         </div>
      </div>
   </PageLoader>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import ReportsAPI from "@/data/api-layer/reportsApi";
import GenomeApi from "@/data/api-layer/genomeApi";
import SnpApi from "@/data/api-layer/snpApi";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { toSnakeCase } from "@/js/helpers/object.helper";
import axios from "axios";

const loadErrorMessage = ref<string>("");
const loadErrors: Ref<Array<Error>> = ref([]);
const isLoading = ref<boolean>(true);
const $route = useRoute();
const { t: $t } = useI18n();

const state = reactive({
   report: null,
   genomes: [],
   snps: {},
   snp_sources: {},
});

// lifecycle
//-----------------------------------------------
onMounted(async () => {
   await fetchReportData();
});

// computed
// -----------------------------------------
const isLocalDevAndNotE2e = computed(() => {
   // we are running locally and not in Cypress mode (This should be changed so the endpoint on the BE returns this list instead)
   return import.meta.env.MODE === "development" && !(window as any).Cypress;
});

const readableCategory = computed(() => state.report.category.replace("_", " & "));

const hasGenome = computed(() => {
   // Always return true for dev work on localhost
   return isLocalDevAndNotE2e.value || state.genomes.length > 0;
});

const showPersonalisation = computed(() => {
   return !!hasGenome.value && !!state?.report?.cardContent;
});

const isConditionReport = computed(() => {
   // Although we may on longer categorise reports by 'human body', 'senses & reactions' and 'conditions', it's still
   // useful to know which reports are on specific medical conditions as these will need an extra note that this is not a diagnosis
   return state.report?.category === "conditions";
});

// methods
// -----------------------------------------
// fetch report data
async function fetchReportData() {
   try {
      const reportKey = $route.params.key as string;

      const response = await ReportsAPI.getOneByKey(reportKey);

      state.report = response;

      // Temporary - on the participant site, hide reports which do not have card content defined in the database
      // This is because initially only 5 reports have been brought over to the new environment.
      if (!state.report.cardContent) {
         loadErrors.value.push(new Error("Report does not have card content"));
         loadErrorMessage.value = $t("validation.report_not_found");
         return;
      }

      // Check to see if the user has a genome
      const genomes = await GenomeApi.readMyGenomes();
      const myGenome = genomes[0];
      if (myGenome) state.genomes.push(myGenome);

      // If the user does have a genome, fetch the snps required for this report
      // hasGenome will also be true if we are developing locally. In this case exampleSnps will be fetched
      if (hasGenome.value) {
         const snpKeys = Object.keys(state.report.snpInfo);
         const snpsToFetch = [];

         // The key may either be a single rsid, or multiple rsids joined with "_"
         for (let i = 0; i < snpKeys.length; i++) {
            // In this instance we want to keep the keys in snake case where they have been converted
            // This is because we need to use "_" as the split point for where multiple rsids are part of one key
            // (e.g. in the lactose tolerance report where certain variants in one of 3 positions is likely to indicate lactose tolerance)
            const snakeInnerKey = toSnakeCase(snpKeys[i]);
            const innerKeys = snakeInnerKey.split("_");

            for (let j = 0; j < innerKeys.length; j++) {
               // return a set of unique rsids
               if (!snpsToFetch.includes(innerKeys[j])) snpsToFetch.push(innerKeys[j]);
            }
         }

         // Fetch the snps that are needed for this report and save them to state
         state.snps = {};
         const fetchedSnps = await SnpApi.readMySnps(snpsToFetch);

         if (!fetchedSnps) return;

         for (let i = 0; i < fetchedSnps.length; i++) {
            state.snps[`${fetchedSnps[i].rsid}`] = fetchedSnps[i].value;
            state.snp_sources[`${fetchedSnps[i].rsid}`] = fetchedSnps[i].source;
         }
      }
   } catch (e: any) {
      loadErrors.value.push(e as Error);
      if (axios.isAxiosError(e) && e.response?.status === 404) {
         loadErrorMessage.value = $t("validation.report_not_found");
      }
   } finally {
      isLoading.value = false;
   }
}

function getSnpContext() {
   // data passed as context to RichTextRenderer and as props to SNPsNode
   return {
      report: state.report,
      snps: state.snps,
      snpSources: state.snp_sources,
      hasGenome: hasGenome.value,
   };
}
</script>

<style scoped lang="scss">
.refs-and-glossary-section {
   @apply flex flex-col md:flex-row items-start justify-start gap-5;
}
</style>
